/**
 * Menus - Dropdown/flyout menu system.
 * Manages hierarchical menus with hover interactions and positioning.
 */

// Declare external dependencies

// Module flag
const _MENUS_JS_ = true;

// Types
interface MenuProps {
  hover?: string;
  vertical?: boolean;
  top?: number;
  left?: number;
  bottom?: number;
  right?: number;
  justify?: "left" | "right";
}

interface MenuData {
  level: number;
  hover: string;
  vertical: boolean;
  top: number;
  left: number;
  bottom: number;
  right: number;
  justify: "left" | "right";
}

interface MenusInstance {
  addMenus: (level: number, props: MenuProps | null, ...menuIds: string[]) => void;
  hideAllMenusFrom: (startLevel: number) => void;
}

/**
 * Creates a MenuData object with default values.
 *
 * @param level - The menu hierarchy level
 * @param props - Optional configuration properties
 * @returns The MenuData object
 */
function createMenuData(level: number, props?: MenuProps | null): MenuData {
  if (props) {
    return {
      level: level,
      hover: props.hover ? props.hover : "defaultHover",
      vertical: props.vertical !== false,
      top: props.top ? props.top : 0,
      left: props.left ? props.left : 0,
      bottom: props.bottom ? props.bottom : 0,
      right: props.right ? props.right : 0,
      justify: props.justify ? props.justify : "left"
    };
  } else {
    return {
      level: level,
      hover: "defaultHover",
      vertical: true,
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      justify: "left"
    };
  }
}

/**
 * Menus constructor - creates a menu management instance.
 */
function Menus(this: MenusInstance): void {
  let timeOn: ReturnType<typeof setTimeout> | null = null;
  const menuDatas: Record<string, MenuData> = {};
  const currentMenu: (HTMLElement | undefined)[] = [];

  const self = this;

  /**
   * Gets the submenu element ID for a menu item.
   */
  function getSubMenuName(menu: HTMLElement): string {
    return menu.id + 'SubMenu';
  }

  /**
   * Gets the MenuData for a menu item.
   */
  function getMenuData(menu: HTMLElement): MenuData {
    return menuDatas[menu.id];
  }

  /**
   * Hides a menu and its submenu.
   */
  function hideMenu(menu: HTMLElement): void {
    menu.classList.remove(getMenuData(menu).hover);
    const subMenu = document.getElementById(getSubMenuName(menu));
    if (subMenu) {
      subMenu.style.visibility = "hidden";
    }
  }

  /**
   * Schedules hiding all menus after a delay.
   */
  function hideAllMenusDelay(): void {
    timeOn = setTimeout(function () { self.hideAllMenusFrom(0); }, 600);
  }

  /**
   * Shows a menu and positions its submenu.
   */
  function showMenu(menu: HTMLElement): void {
    const menuData = getMenuData(menu);
    if (currentMenu[menuData.level] && currentMenu[menuData.level] !== menu) {
      self.hideAllMenusFrom(menuData.level);
    }

    if (timeOn != null) {
      clearTimeout(timeOn);
      timeOn = null;
    }

    const subMenu = document.getElementById(getSubMenuName(menu));
    if (subMenu) {
      const subMenuStyle = subMenu.style;
      const browser = getInnerSize();
      const scollPos = getScrollPosition();
      const menuPos = findPosition(menu);
      const menuSize = getObjectSize(menu);
      let subMenuW: number;
      let subMenuH: number;
      let loopcounter = 0;

      menuSize.width += menuData.left + menuData.right;
      menuSize.height += menuData.top + menuData.bottom;
      menuPos.left += -menuData.left;
      menuPos.top += -menuData.top;

      if (menuData.vertical) {
        do {
          loopcounter++;

          let x = menuPos.left;
          subMenuW = getObjectWidth(subMenu);
          const right = x + subMenuW;

          if ((browser.width + scollPos.left < right) || menuData.justify === "right") {
            const x2 = menuSize.width - subMenuW + menuData.left;
            if (x + x2 >= 0) {
              x += x2;
            }
          }

          subMenuStyle.left = x + 'px';

        } while (subMenuW !== getObjectWidth(subMenu) && loopcounter < 5);

        let y = menuPos.top;
        subMenuH = getObjectHeight(subMenu);
        const bottom = y + menuSize.height + subMenuH;

        if (browser.height + scollPos.top < bottom) {
          y -= subMenuH;
        } else {
          y += menuSize.height;
        }

        if (y < scollPos.top) {
          y = scollPos.top;
        }

        subMenuStyle.top = y + 'px';
      } else {
        do {
          loopcounter++;

          let x = menuPos.left;
          subMenuW = getObjectWidth(subMenu);
          const right = x + subMenuW + menuSize.width;

          if (browser.width + scollPos.left < right || menuData.justify === "right") {
            x -= subMenuW + menuData.left;
          } else {
            x += menuSize.width;
          }
          subMenuStyle.left = x + 'px';

        } while (subMenuW !== getObjectWidth(subMenu) && loopcounter < 5);

        let y = menuPos.top;
        subMenuH = getObjectHeight(subMenu);
        const bottom = y + menuSize.height + subMenuH;

        if (browser.height + scollPos.top < bottom) {
          y += menuSize.height - (subMenuH - 1);
        }

        if (y < scollPos.top) {
          y = scollPos.top;
        }
        subMenuStyle.top = y + 'px';
      }
      subMenuStyle.visibility = "visible";
    }
    menu.classList.add(getMenuData(menu).hover);
    currentMenu[menuData.level] = menu;
  }

  /**
   * Adds menus to the menu system.
   *
   * @param level - The hierarchy level for these menus
   * @param props - Menu configuration properties
   * @param menuIds - IDs of menu elements to register
   */
  this.addMenus = function (level: number, props: MenuProps | null, ...menuIds: string[]): void {
    const menuData = createMenuData(level, props);
    for (let i = 0; i < menuIds.length; i++) {
      menuDatas[menuIds[i]] = menuData;
      const menuEl = document.getElementById(menuIds[i]);
      if (menuEl) {
        menuEl.addEventListener('mouseover', function (this: HTMLElement) {
          showMenu(this);
        });
        menuEl.addEventListener('mouseout', function () {
          hideAllMenusDelay();
        });
      }
    }
  };

  /**
   * Hides all menus starting from a given level.
   *
   * @param startLevel - The level to start hiding from
   */
  this.hideAllMenusFrom = function (startLevel: number): void {
    for (let i = startLevel; currentMenu[i]; i++) {
      hideMenu(currentMenu[i]!);
    }
  };
}

// Create the global menus instance
const menus: MenusInstance = new (Menus as any)();

// Export for module usage
export {
  _MENUS_JS_,
  Menus,
  menus,
  createMenuData
};

// Export types
export type {
  MenuProps,
  MenuData,
  MenusInstance
};

// Expose on globalThis for backwards compatibility
(globalThis as any)._MENUS_JS_ = _MENUS_JS_;
(globalThis as any).Menus = Menus;
(globalThis as any).menus = menus;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("menus.js");
}
